import { summarizeFileWithGemini } from "../utils/geminiSummarization.js";

import { summarizeMultipleFilesWithGemini } from "../utils/geminiSummarization.js";

async function resizeImageBlob(blob, width = 1280, height = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Create canvas and draw resized image
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      // Clear canvas and draw image resized to target dimensions
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Convert canvas to blob
      canvas.toBlob(
        (resizedBlob) => {
          if (resizedBlob) {
            resolve(resizedBlob);
          } else {
            reject(new Error("Canvas toBlob returned null"));
          }
        },
        blob.type,
        1
      );
    };
    img.onerror = (error) => {
      reject(error);
    };

    // Create a URL for the blob and set as image source
    img.src = URL.createObjectURL(blob);
  });
}

export async function singleClassify(downloadURL, fileName) {
  console.log(
    "singleClassify called with downloadURL:",
    downloadURL,
    "fileName:",
    fileName
  );
  try {
    // Fetch the file blob from the download URL
    const response = await fetch(downloadURL);
    if (!response.ok) {
      throw new Error("Failed to fetch file from URL: " + response.statusText);
    }
    let blob = await response.blob();

    // Resize image blob if it is an image type
    if (blob.type.startsWith("image/")) {
      blob = await resizeImageBlob(blob, 800, 800);
    }

    let uploadFileName = fileName;
    if (fileName.toLowerCase().endsWith(".pdf")) {
      uploadFileName = fileName.replace(/\.[^/.]+$/, "") + "_page1.png";
    }

    // Prepare form data for classifier
    const formData = new FormData();
    formData.append("file", blob, uploadFileName);

    // Start both API calls at the same time
    console.log("summary is sending to gemini....");
    const summaryPromise = summarizeFileWithGemini(blob);

    const apiUrl =
      "https://classify-api-822944814773.asia-southeast1.run.app/classify-single";
    console.log("Sending POST request to API URL:", apiUrl);
    const classifyPromise = fetch(apiUrl, {
      method: "POST",
      body: formData,
    });

    // Wait for both to finish
    const [summary, apiResponse] = await Promise.all([
      summaryPromise,
      classifyPromise,
    ]);
    console.log("Both summary and classifier finished.");

    if (!apiResponse.ok) {
      const errorText = await apiResponse.text();
      throw new Error("API error: " + apiResponse.status + " - " + errorText);
    }

    const result = await apiResponse.json();
    console.log("API response received:", result);

    // Append summary directly into the classification result object
    result.summary = summary;

    return result;
  } catch (error) {
    console.error("singleClassify error:", error);
    throw error;
  }
}

export async function batchClassify(files) {
  console.log("batchClassify called with files:", files);

  try {
    // Step 1: Fetch all file blobs in parallel
    const blobResults = await Promise.all(
      files.map(async (file) => {
        const url =
          file.type === "application/pdf" && file.downloadPDFURL
            ? file.downloadPDFURL
            : file.downloadURL;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${file.name}`);
        }
        let blob = await response.blob();

        // Resize image blob if it is an image type
        if (blob.type.startsWith("image/")) {
          blob = await resizeImageBlob(blob, 800, 800);
        }

        return { name: file.name, blob };
      })
    );
    console.log("All file blobs fetched.");

    // Step 2: Start all summary requests in parallel
    console.log("Starting all summary requests to gemini...");
    const summaryPromises = blobResults.map(({ blob }, i) => {
      console.log(`Summarizing file #${i + 1}: ${files[i].name}`);
      return summarizeFileWithGemini(blob);
    });

    // Step 3: Prepare form data for batch classification
    const formData = new FormData();
    blobResults.forEach(({ name, blob }) => {
      formData.append("files", blob, name);
    });

    // Step 4: Start the batch classifier request
    const apiUrl =
      "https://classify-api-822944814773.asia-southeast1.run.app/classify-batch";
    console.log("Sending batch POST request to API URL:", apiUrl);

    // Step 5: Await both summarization and classifier concurrently
    const [summaries, apiResponse] = await Promise.all([
      Promise.all(summaryPromises),
      fetch(apiUrl, { method: "POST", body: formData }),
    ]);
    console.log("Both summaries and batch classification finished.");

    // Step 6: Check classifier response
    if (!apiResponse.ok) {
      const errorText = await apiResponse.text();
      throw new Error(`API batch error: ${apiResponse.status} - ${errorText}`);
    }
    const result = await apiResponse.json();
    console.log("batchClassify API response:", result);

    // Step 7: Attach summaries to each file result (by index)
    if (result.files && Array.isArray(result.files)) {
      for (let i = 0; i < result.files.length; i++) {
        result.files[i].summary = summaries[i] || "";
      }
    }

    // Step 8: Return the combined results
    return result.files;
  } catch (error) {
    console.error("batchClassify error:", error);
    throw error;
  }
}

export async function multiPageClassify(formData, pageBlobs) {
  console.log("multiPageClassify called with formData");

  // Resize image blobs in pageBlobs if they are images
  const resizedPageBlobs = await Promise.all(
    pageBlobs.map(async (blob) => {
      if (blob.type.startsWith("image/")) {
        return await resizeImageBlob(blob, 800, 800);
      }
      return blob;
    })
  );

  // Start both API calls concurrently
  console.log("summary is sending to gemini....");
  const summaryPromise = summarizeMultipleFilesWithGemini(resizedPageBlobs);

  const apiUrl =
    "https://classify-api-822944814773.asia-southeast1.run.app/classify";
  console.log("Sending POST request to multi-page classify API URL:", apiUrl);
  const classifyPromise = fetch(apiUrl, {
    method: "POST",
    body: formData,
  });

  let overviewSummary, apiResponse;

  try {
    // Wait for both to finish
    [overviewSummary, apiResponse] = await Promise.all([
      summaryPromise,
      classifyPromise,
    ]);
    console.log("Both summary and classifier finished.");
  } catch (error) {
    console.error("Error in either summarization or classification:", error);
    throw error;
  }

  // Handle classifier API response errors
  if (!apiResponse.ok) {
    const errorText = await apiResponse.text();
    console.error(
      `API multi-page classify error: ${apiResponse.status} - ${errorText}`
    );
    throw new Error(
      `API multi-page classify error: ${apiResponse.status} - ${errorText}`
    );
  }

  const result = await apiResponse.json();
  result.summary = overviewSummary;
  console.log("multiPageClassify API response:", result);

  return result;
}
